import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { CategoryDialogComponent } from './category-dialog/category-dialog.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

export interface SubSubCategory {
  id: number;
  subSubCategoryId: string;
  subCategoryId: string;
  description: string;
}

export interface SubCategory {
  id: number;
  subCategoryId: string;
  categoryId: string;
  description: string;
  subSubCategories: SubSubCategory[];
  expanded: boolean;
}

export interface Category {
  id: number;
  categoryId: string;
  description: string;
  subCategories: SubCategory[];
  expanded: boolean;
}

@Component({
  selector: 'app-category-tree',
  templateUrl: './category-tree.component.html',
  styleUrls: ['./category-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoryTreeComponent implements OnInit {
  categories: Category[] = [];
  showDeleteDialog = false;
  deleteTarget: { type: 'category' | 'subcategory' | 'subsubcategory'; id: number } | null = null;

  private _loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private toasterService = inject(ToasterService);

  ngOnInit(): void {
    this.loadAll();
  }

  async loadAll(): Promise<void> {
    this._loaderService.showLoader();
    try {
      const [cats, subs, subSubs] = await Promise.all([
        lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/categories`)),
        lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/subcategories`)),
        lastValueFrom(this.apiService.getByUrl(`${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/subsubcategories`))
      ]);

      const rawCats: any[] = this.extractArray(cats);
      const rawSubs: any[] = this.extractArray(subs);
      const rawSubSubs: any[] = this.extractArray(subSubs);

      this.categories = rawCats.map(cat => {
        const catId = cat.categoryId || cat.CategoryId || cat.categoryID || '';
        const subCats = rawSubs
          .filter(s => (s.categoryId || s.CategoryId || s.categoryID || '') === catId)
          .map(sub => {
            const subId = sub.subCategoryId || sub.SubCategoryId || sub.subCategoryID || '';
            return {
              id: sub.id || sub.ID,
              subCategoryId: subId,
              categoryId: catId,
              description: sub.description || sub.Description || sub.subCategoryId || '',
              expanded: false,
              subSubCategories: rawSubSubs
                .filter(ss => (ss.subCategoryId || ss.SubCategoryId || ss.subCategoryID || '') === subId)
                .map(ss => ({
                  id: ss.id || ss.ID,
                  subSubCategoryId: ss.subSubCategoryId || ss.SubSubCategoryId || ss.subSubCategoryID || '',
                  subCategoryId: subId,
                  description: ss.description || ss.Description || ss.subSubCategoryId || ''
                }))
            } as SubCategory;
          });

        return {
          id: cat.id || cat.ID,
          categoryId: catId,
          description: cat.description || cat.Description || catId,
          expanded: false,
          subCategories: subCats
        } as Category;
      });

      this.cdr.markForCheck();
    } catch (error) {
      console.error('[Category Tree] Error loading data:', error);
      this.toasterService.showError('Failed to load category tree', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  toggleCategory(cat: Category): void {
    cat.expanded = !cat.expanded;
    this.cdr.markForCheck();
  }

  toggleSubCategory(sub: SubCategory): void {
    sub.expanded = !sub.expanded;
    this.cdr.markForCheck();
  }

  // --- Add ---
  openAddCategory(): void {
    const ref = this.dialog.open(CategoryDialogComponent, {
      width: '480px', maxWidth: '95vw', disableClose: false,
      panelClass: 'category-dialog',
      data: { mode: 'add-category' }
    });
    ref.afterClosed().subscribe(r => { if (r) this.loadAll(); });
  }

  openAddSubCategory(cat: Category): void {
    const ref = this.dialog.open(CategoryDialogComponent, {
      width: '480px', maxWidth: '95vw', disableClose: false,
      panelClass: 'category-dialog',
      data: { mode: 'add-subcategory', parentId: cat.categoryId }
    });
    ref.afterClosed().subscribe(r => { if (r) this.loadAll(); });
  }

  openAddSubSubCategory(sub: SubCategory): void {
    const ref = this.dialog.open(CategoryDialogComponent, {
      width: '480px', maxWidth: '95vw', disableClose: false,
      panelClass: 'category-dialog',
      data: { mode: 'add-subsubcategory', parentId: sub.subCategoryId }
    });
    ref.afterClosed().subscribe(r => { if (r) this.loadAll(); });
  }

  // --- Edit ---
  openEditCategory(cat: Category): void {
    const ref = this.dialog.open(CategoryDialogComponent, {
      width: '480px', maxWidth: '95vw', disableClose: false,
      panelClass: 'category-dialog',
      data: { mode: 'edit-category', item: cat }
    });
    ref.afterClosed().subscribe(r => { if (r) this.loadAll(); });
  }

  openEditSubCategory(sub: SubCategory): void {
    const ref = this.dialog.open(CategoryDialogComponent, {
      width: '480px', maxWidth: '95vw', disableClose: false,
      panelClass: 'category-dialog',
      data: { mode: 'edit-subcategory', item: sub }
    });
    ref.afterClosed().subscribe(r => { if (r) this.loadAll(); });
  }

  openEditSubSubCategory(ss: SubSubCategory): void {
    const ref = this.dialog.open(CategoryDialogComponent, {
      width: '480px', maxWidth: '95vw', disableClose: false,
      panelClass: 'category-dialog',
      data: { mode: 'edit-subsubcategory', item: ss }
    });
    ref.afterClosed().subscribe(r => { if (r) this.loadAll(); });
  }

  // --- Delete ---
  confirmDelete(type: 'category' | 'subcategory' | 'subsubcategory', id: number): void {
    this.deleteTarget = { type, id };
    this.showDeleteDialog = true;
    this.cdr.markForCheck();
  }

  cancelDelete(): void {
    this.showDeleteDialog = false;
    this.deleteTarget = null;
    this.cdr.markForCheck();
  }

  async executeDelete(): Promise<void> {
    if (!this.deleteTarget) return;
    this.showDeleteDialog = false;

    const endpointMap: Record<string, string> = {
      category: 'categories',
      subcategory: 'subcategories',
      subsubcategory: 'subsubcategories'
    };

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.Lookups}/${endpointMap[this.deleteTarget.type]}/${this.deleteTarget.id}`;
      await lastValueFrom(this.apiService.deleteByUrl(url, {}, {}));
      this.toasterService.showSuccess('Deleted successfully', 'Success');
      this.loadAll();
    } catch (error: any) {
      const msg = error?.error?.errorMessageEn || error?.message || 'Delete failed';
      this.toasterService.showError(msg, 'Error');
      this._loaderService.hideLoader();
    } finally {
      this.deleteTarget = null;
      this.cdr.markForCheck();
    }
  }
}
